/**
 * Bundle PDP Controller
 *
 * Handles fetching and displaying bundle component data on the PDP:
 * 1. Fetches bundle component structure via Storefront API
 * 2. Fetches swatch metafields via Section Rendering API
 * 3. Injects component product names and swatch hex colors into DOM
 *
 * @requires variant-picker custom element to be present on page
 */

import { VariantUpdateEvent } from "@theme/events";

const CONFIG = {
  API_VERSION: "2025-10",
  ENDPOINT: "/api/2025-10/graphql.json",
  MAX_RETRIES: 3,
  RETRY_DELAY_MS: 1000,
  TOAST_DURATION_MS: 5000,
  CACHE_KEY_PREFIX: "bundle_pdp_",
  CACHE_TTL_MS: 30 * 60 * 1000, // 30 minutes
};

/**
 * GraphQL query to fetch bundle component data for a variant
 * Includes image fields with 150x150 center crop transform for thumbnails
 */
const BUNDLE_COMPONENTS_QUERY = `
  query BundleComponents($variantId: ID!) {
    node(id: $variantId) {
      ... on ProductVariant {
        id
        requiresComponents
        components(first: 20) {
          nodes {
            quantity
            productVariant {
              id
              title
              sku
              availableForSale
              selectedOptions {
                name
                value
              }
              image {
                url(transform: { maxWidth: 150, maxHeight: 150, crop: CENTER })
                altText
              }
              product {
                id
                title
                handle
                featuredImage {
                  url(transform: { maxWidth: 150, maxHeight: 150, crop: CENTER })
                  altText
                }
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * GraphQL query to fetch ALL bundle variants and their components
 * Used to build a mapping of component selections → bundle variant ID
 */
const BUNDLE_ALL_VARIANTS_QUERY = `
  query BundleAllVariants($productId: ID!) {
    node(id: $productId) {
      ... on Product {
        id
        variants(first: 100) {
          nodes {
            id
            availableForSale
            components(first: 20) {
              nodes {
                productVariant {
                  id
                  product {
                    handle
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * Converts a numeric ID to a Shopify GID
 * @param {string|number} id
 * @param {'ProductVariant'} type
 * @returns {string}
 */
const toGid = (id, type = "ProductVariant") => {
  const idStr = String(id);
  if (idStr.startsWith("gid://")) return idStr;
  return `gid://shopify/${type}/${idStr}`;
};

/**
 * Converts a numeric ID to a Shopify Product GID
 * @param {string|number} id
 * @returns {string}
 */
const toProductGid = (id) => toGid(id, "Product");

/**
 * Extracts numeric ID from a Shopify GID
 * @param {string} gid
 * @returns {string}
 */
const fromGid = (gid) => {
  if (!gid) return "";
  const match = gid.match(/\/(\d+)$/);
  return match ? match[1] : gid;
};

/**
 * Sleep utility for retry delays
 * @param {number} ms
 * @returns {Promise<void>}
 */
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Fetches all bundle variants and builds a mapping of component selections → bundle variant ID
 * @param {string|number} productId
 * @returns {Promise<Map<string, string>|null>} Map where key is sorted component variant IDs, value is bundle variant ID
 */
async function fetchBundleVariantMap(productId) {
  const gid = toGid(productId, "Product");

  try {
    const response = await fetch(CONFIG.ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query: BUNDLE_ALL_VARIANTS_QUERY,
        variables: { productId: gid },
      }),
    });

    if (!response.ok) {
      console.error(
        "[BundlePDP] Failed to fetch bundle variant map:",
        response.status,
      );
      return null;
    }

    const data = await response.json();
    const product = data?.data?.node;

    if (!product?.variants?.nodes) {
      console.warn("[BundlePDP] No variants found for bundle product");
      return null;
    }

    // Build mapping: sorted component variant IDs → bundle variant ID
    const variantMap = new Map();

    for (const bundleVariant of product.variants.nodes) {
      if (!bundleVariant.components?.nodes?.length) continue;

      // Create a key from sorted component variant IDs
      const componentIds = bundleVariant.components.nodes
        .map((c) => fromGid(c.productVariant.id))
        .sort()
        .join(",");

      variantMap.set(componentIds, fromGid(bundleVariant.id));

      // Also store by handle → variant ID for individual lookup
      bundleVariant.components.nodes.forEach((c) => {
        const handle = c.productVariant.product.handle;
        const componentVariantId = fromGid(c.productVariant.id);
        // Store handle-based mapping for this bundle variant
        const handleKey = `${fromGid(bundleVariant.id)}:${handle}`;
        variantMap.set(handleKey, componentVariantId);
      });
    }

    console.log(
      "[BundlePDP] Built bundle variant map with",
      variantMap.size,
      "entries",
    );
    return variantMap;
  } catch (error) {
    console.error("[BundlePDP] Error fetching bundle variant map:", error);
    return null;
  }
}

/**
 * Fetches bundle components from Storefront API
 * @param {string|number} variantId
 * @returns {Promise<{requiresComponents: boolean, components: Array}|null>}
 */
async function fetchBundleComponents(variantId) {
  const gid = toGid(variantId, "ProductVariant");

  try {
    const response = await fetch(CONFIG.ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query: BUNDLE_COMPONENTS_QUERY,
        variables: { variantId: gid },
      }),
    });

    if (!response.ok) {
      console.error(
        `[BundlePDP] Storefront API HTTP Error: ${response.status}`,
      );
      return null;
    }

    const { data, errors } = await response.json();

    if (errors) {
      console.error("[BundlePDP] Storefront API GraphQL Errors:", errors);
      return null;
    }

    if (!data?.node) {
      console.warn("[BundlePDP] No variant data returned");
      return null;
    }

    return {
      requiresComponents: data.node.requiresComponents || false,
      components: data.node.components?.nodes || [],
    };
  } catch (error) {
    console.error("[BundlePDP] Storefront API Network Error:", error);
    return null;
  }
}

/**
 * Fetches swatch metafield data via Section Rendering API with retry logic
 * @param {string} productHandle
 * @param {number} attempt
 * @returns {Promise<Object|null>}
 */
async function fetchSwatchData(productHandle, attempt = 1) {
  const url = `/products/${productHandle}?section_id=bundle-component-swatches`;

  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const script = doc.querySelector("[data-bundle-component-swatches]");

    if (!script) {
      throw new Error("No swatch data script found in response");
    }

    const parsedData = JSON.parse(script.textContent);
    console.log(
      `[BundlePDP] Raw swatch data for ${productHandle}:`,
      parsedData,
    );

    if (parsedData?.variants?.length > 0) {
      parsedData.variants.forEach((variant) => {
        if (
          variant?.hexColor &&
          variant?.hexColor.length > 0 &&
          variant?.hexColor.split(";").length > 1
        ) {
          variant.hexColor = variant.hexColor.split(";")[0];
        }
      });
    }

    return parsedData;
  } catch (error) {
    console.warn(
      `[BundlePDP] Swatch fetch attempt ${attempt} failed for ${productHandle}:`,
      error.message,
    );

    if (attempt < CONFIG.MAX_RETRIES) {
      const delay = CONFIG.RETRY_DELAY_MS * Math.pow(2, attempt - 1);
      console.info(`[BundlePDP] Retrying in ${delay}ms...`);
      await sleep(delay);
      return fetchSwatchData(productHandle, attempt + 1);
    }

    console.error(
      `[BundlePDP] All ${CONFIG.MAX_RETRIES} attempts failed for ${productHandle}`,
    );
    return null;
  }
}

/**
 * Shows error toast notification
 * @param {string} message
 */
function showErrorToast(message) {
  let toast = document.querySelector(".bundle-pdp-toast");

  if (!toast) {
    toast = document.createElement("div");
    toast.className = "bundle-pdp-toast";
    toast.setAttribute("role", "alert");
    toast.setAttribute("aria-live", "polite");
    document.body.appendChild(toast);
  }

  toast.textContent = message;
  toast.classList.add("visible");

  setTimeout(() => {
    toast.classList.remove("visible");
  }, CONFIG.TOAST_DURATION_MS);
}

/**
 * SessionStorage cache helpers
 */
const BundleCache = {
  /**
   * Gets cached data from sessionStorage
   * @param {string} key
   * @returns {Object|null}
   */
  get(key) {
    try {
      const cached = sessionStorage.getItem(CONFIG.CACHE_KEY_PREFIX + key);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp > CONFIG.CACHE_TTL_MS) {
        sessionStorage.removeItem(CONFIG.CACHE_KEY_PREFIX + key);
        return null;
      }
      return data;
    } catch {
      return null;
    }
  },

  /**
   * Sets data in sessionStorage cache
   * @param {string} key
   * @param {Object} data
   */
  set(key, data) {
    try {
      sessionStorage.setItem(
        CONFIG.CACHE_KEY_PREFIX + key,
        JSON.stringify({ data, timestamp: Date.now() }),
      );
    } catch (e) {
      console.warn("[BundlePDP] Cache write failed:", e.message);
    }
  },
};

/**
 * Bundle Dropdown Controller class
 * Handles the new dropdown-based variant picker for bundles
 */
class BundleDropdownController {
  /**
   * @param {HTMLElement} pickerContainer - The bundle-variant-picker container
   */
  constructor(pickerContainer) {
    this.container = pickerContainer;
    this.productId = pickerContainer.dataset.productId;
    this.productUrl = pickerContainer.dataset.productUrl;
    this.sectionId = pickerContainer.dataset.sectionId;
    this.componentsContainer = pickerContainer.querySelector(
      "[data-bundle-components]",
    );
    this.componentData = null;
    this.swatchDataMap = null;
    this.initialized = false;
    this.dropdowns = new Map(); // Track dropdown instances
    this.componentVariants = new Map(); // Store variants per component for lookup
    this.bundleVariantMap = null; // Maps component selections → bundle variant ID
    this.allBundleVariants = []; // All bundle variants with their components
    this.variantUpdateAbortController = null;
  }

  /**
   * Initializes the controller
   * @param {string|number} variantId
   */
  async init(variantId) {
    console.log("[BundleDropdown] init() called with variantId:", variantId);

    if (!variantId) {
      console.warn("[BundleDropdown] No variant ID provided");
      return;
    }

    if (this.initialized) {
      console.info("[BundleDropdown] Already initialized, skipping");
      return;
    }

    console.log("[BundleDropdown] Fetching bundle data...");

    // Check sessionStorage cache first
    const cacheKey = `product_${this.productId}_variant_${variantId}`;
    let bundleData = BundleCache.get(cacheKey);

    if (
      bundleData &&
      (!bundleData.components || bundleData.components.length === 0)
    ) {
      bundleData = null;
    }

    if (!bundleData) {
      console.log("[BundleDropdown] No cache, fetching from API...");
      bundleData = await fetchBundleComponents(variantId);
      console.log("[BundleDropdown] API response:", bundleData);
      if (bundleData?.requiresComponents && bundleData.components?.length > 0) {
        BundleCache.set(cacheKey, bundleData);
      }
    } else {
      console.log("[BundleDropdown] Using cached bundle data:", bundleData);
    }

    if (!bundleData?.requiresComponents || !bundleData.components?.length) {
      console.warn(
        "[BundleDropdown] No valid bundle components, showing empty state",
      );
      this.showEmptyState();
      return;
    }

    console.log(
      "[BundleDropdown] Bundle has",
      bundleData.components.length,
      "components",
    );

    this.componentData = bundleData.components;

    // Fetch swatch data for each component
    const productHandles = [
      ...new Set(
        bundleData.components.map((c) => c.productVariant.product.handle),
      ),
    ];

    const swatchResults = await Promise.all(
      productHandles.map(async (handle) => {
        const cachedSwatch = BundleCache.get(`swatch_${handle}`);
        if (cachedSwatch) return { handle, data: cachedSwatch };

        const data = await fetchSwatchData(handle);
        if (data) BundleCache.set(`swatch_${handle}`, data);
        return { handle, data };
      }),
    );

    const failures = swatchResults.filter((r) => r.data === null);
    if (failures.length > 0) {
      showErrorToast(
        "Unable to load some product information. Please refresh the page.",
      );
    }

    this.swatchDataMap = new Map(
      swatchResults
        .filter((r) => r.data !== null)
        .map((r) => [r.handle, r.data]),
    );

    // Fetch all bundle variants to build component → bundle variant mapping
    await this.fetchAllBundleVariants();

    // Build the UI
    this.renderComponents();
    this.container.setAttribute("data-loaded", "");
    this.initialized = true;
  }

  /**
   * Shows empty state when no components found
   */
  showEmptyState() {
    if (this.componentsContainer) {
      this.componentsContainer.innerHTML = "";
    }
    this.container.setAttribute("data-loaded", "");
  }

  /**
   * Fetches all bundle variants and stores mapping for variant lookup
   */
  async fetchAllBundleVariants() {
    const gid = `gid://shopify/Product/${this.productId}`;

    try {
      const response = await fetch(CONFIG.ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: BUNDLE_ALL_VARIANTS_QUERY,
          variables: { productId: gid },
        }),
      });

      if (!response.ok) {
        console.error(
          "[BundleDropdown] Failed to fetch all bundle variants:",
          response.status,
        );
        return;
      }

      const data = await response.json();
      const product = data?.data?.node;

      if (!product?.variants?.nodes) {
        console.warn("[BundleDropdown] No bundle variants found");
        return;
      }

      // Store all bundle variants with their component mappings
      this.allBundleVariants = product.variants.nodes.map((bundleVariant) => {
        const componentMap = {};
        if (bundleVariant.components?.nodes) {
          bundleVariant.components.nodes.forEach((c) => {
            const handle = c.productVariant.product.handle;
            componentMap[handle] = fromGid(c.productVariant.id);
          });
        }
        return {
          id: fromGid(bundleVariant.id),
          available: bundleVariant.availableForSale,
          components: componentMap,
        };
      });

      console.log(
        "[BundleDropdown] Loaded",
        this.allBundleVariants.length,
        "bundle variants",
      );
    } catch (error) {
      console.error("[BundleDropdown] Error fetching bundle variants:", error);
    }
  }

  /**
   * Gets the set of allowed component variant IDs for a given product handle.
   * Only variants that appear in at least one bundle variant configuration are allowed.
   * @param {string} handle - Product handle
   * @returns {Set<string>} Set of allowed variant IDs
   */
  getAllowedVariantIds(handle) {
    const allowedIds = new Set();

    for (const bundleVariant of this.allBundleVariants) {
      const variantId = bundleVariant.components[handle];
      if (variantId) {
        allowedIds.add(variantId);
      }
    }

    return allowedIds;
  }

  /**
   * Finds the bundle variant that matches the current component selections
   * @returns {string|null} Bundle variant ID or null
   */
  findMatchingBundleVariant() {
    const selections = this.getAllComponentSelections();
    const selectionHandles = Object.keys(selections);

    if (selectionHandles.length === 0) {
      console.warn("[BundleDropdown] No component selections to match");
      return null;
    }

    console.log(
      "[BundleDropdown] Finding bundle variant for selections:",
      selections,
    );

    // Find bundle variant where all components match
    for (const bundleVariant of this.allBundleVariants) {
      let matches = true;

      for (const [handle, variantId] of Object.entries(selections)) {
        if (bundleVariant.components[handle] !== variantId) {
          matches = false;
          break;
        }
      }

      if (matches) {
        console.log(
          "[BundleDropdown] Found matching bundle variant:",
          bundleVariant.id,
        );
        return bundleVariant.id;
      }
    }

    console.warn(
      "[BundleDropdown] No matching bundle variant found for current selections",
    );
    return null;
  }

  /**
   * Renders all component rows
   */
  renderComponents() {
    if (!this.componentsContainer || !this.componentData) return;

    // Build component rows HTML
    let html = "";

    // Render every component returned for the selected bundle variant. A fixed
    // bundle can contain multiple variants of the same product, so grouping by
    // product handle would incorrectly discard all but the first component.
    this.componentData.forEach((component, componentIndex) => {
      const handle = component.productVariant.product.handle;
      const swatchData = this.swatchDataMap.get(handle);
      const product = component.productVariant.product;
      const selectedVariant = component.productVariant;
      const componentQuantity = Number(component.quantity) || 1;

      // Get image
      const imageData = selectedVariant.image || product.featuredImage;
      const imageUrl = imageData?.url || "";
      const imageAlt = imageData?.altText || product.title;

      // Get allowed variant IDs for this component from bundle configuration
      const allowedVariantIds = this.getAllowedVariantIds(handle);

      // Get variants from swatch data, filtered to only include allowed bundle variants
      const allVariants = swatchData?.variants
        ? Object.entries(swatchData.variants).filter(([variantId]) =>
            allowedVariantIds.has(variantId),
          )
        : [];
      const hasSwatch = swatchData?.hasSwatches;

      // Find selected variant in swatch data
      const selectedVariantId = fromGid(selectedVariant.id);
      const selectedSwatchVariant = swatchData?.variants?.[selectedVariantId];
      const selectedTitle =
        selectedSwatchVariant?.title || selectedVariant.title;
      const selectedDescription = selectedSwatchVariant?.shadeDescription || "";

      // Get product options and determine which have multiple values
      const productOptions = swatchData?.options || [];
      const optionsWithMultipleValues = this.getOptionsWithMultipleValues(
        productOptions,
        allVariants,
      );

      // Use API order for CSS ordering to preserve admin-defined sequence
      const apiOrder = componentIndex;

      html += `
        <div class="bundle-component" data-component-handle="${handle}" data-component-index="${componentIndex}" data-api-order="${apiOrder}" data-selected-variant-id="${selectedVariantId}" style="order: ${apiOrder}">
          <div class="bundle-component__image">
            ${
              imageUrl
                ? `
              <a href="/products/${handle}">
                <img src="${imageUrl}" alt="${this.escapeHtml(imageAlt)}" loading="lazy" width="100" height="100" />
              </a>
            `
                : ""
            }
          </div>
          <div class="bundle-component__content">
            <h5 class="bundle-component__title">
              <a href="/products/${handle}">${this.escapeHtml(product.title)}</a>
            </h5>
            ${
              optionsWithMultipleValues.length > 0
                ? `${this.renderOptionDropdowns(
                    handle,
                    componentIndex,
                    allVariants,
                    selectedVariantId,
                    hasSwatch,
                    optionsWithMultipleValues,
                    swatchData,
                  )}${this.renderComponentQuantityInfo(componentQuantity)}`
                : this.renderSingleVariantInfo(
                    selectedTitle,
                    selectedDescription,
                    componentQuantity,
                  )
            }
          </div>
        </div>
      `;
    });

    this.componentsContainer.innerHTML = html;

    // Mark visually first and last items for CSS styling (since :first-child/:last-child target DOM order, not visual order)
    const components =
      this.componentsContainer.querySelectorAll(".bundle-component");
    if (components.length > 0) {
      let minOrder = Infinity;
      let maxOrder = -Infinity;
      let firstEl = null;
      let lastEl = null;

      components.forEach((el) => {
        const order = parseInt(el.dataset.apiOrder, 10);
        if (order < minOrder) {
          minOrder = order;
          firstEl = el;
        }
        if (order > maxOrder) {
          maxOrder = order;
          lastEl = el;
        }
      });

      if (firstEl) firstEl.classList.add("bundle-component--first");
      if (lastEl) lastEl.classList.add("bundle-component--last");
    }

    // Initialize dropdown behaviors
    this.initializeDropdowns();
  }

  /**
   * Determines which options have multiple unique values
   * @returns {Array<{name: string, position: number, values: string[]}>}
   */
  getOptionsWithMultipleValues(productOptions, variants) {
    const result = [];

    productOptions.forEach((option, index) => {
      const optionKey = `option${index + 1}`;
      const uniqueValues = new Set();

      variants.forEach(([, variantData]) => {
        if (variantData[optionKey]) {
          uniqueValues.add(variantData[optionKey]);
        }
      });

      // Only include options with more than one unique value
      if (uniqueValues.size > 1) {
        result.push({
          name: option.name,
          position: index + 1,
          values: Array.from(uniqueValues),
        });
      }
    });

    return result;
  }

  /**
   * Renders dropdown(s) for component options - one per option with multiple values
   */
  renderOptionDropdowns(
    handle,
    componentIndex,
    variants,
    selectedVariantId,
    hasSwatch,
    optionsWithMultipleValues,
    swatchData,
  ) {
    // Store variants for this component for later lookup
    this.componentVariants.set(handle, variants);

    let html = '<div class="bundle-dropdowns">';

    optionsWithMultipleValues.forEach((option, optionIdx) => {
      const optionKey = `option${option.position}`;

      // Get selected value for this option
      const selectedVariantData = variants.find(
        ([id]) => id === selectedVariantId,
      )?.[1];
      const selectedValue =
        selectedVariantData?.[optionKey] || option.values[0];

      // Build unique option values with their variant data
      const optionValuesMap = new Map();
      variants.forEach(([variantId, variantData]) => {
        const value = variantData[optionKey];
        if (value && !optionValuesMap.has(value)) {
          optionValuesMap.set(value, {
            value,
            variantId,
            hexColor: variantData.hexColor,
            shadeDescription: variantData.shadeDescription,
            available: variantData.available !== false,
          });
        }
      });

      // Determine if this option should show swatches (typically first option / color)
      const showSwatch = hasSwatch && option.position === 1;

      html += this.renderDropdown(
        handle,
        `${componentIndex}-${option.position}`,
        Array.from(optionValuesMap.entries()),
        selectedValue,
        showSwatch,
        option.name,
      );
    });

    html += "</div>";
    return html;
  }

  /**
   * Renders a dropdown selector for a single option
   * @param {string} handle - Product handle
   * @param {string|number} index - Unique index for this dropdown
   * @param {Array} optionValues - Array of [value, data] pairs for this option
   * @param {string} selectedValue - Currently selected option value
   * @param {boolean} hasSwatch - Whether to show color swatches
   * @param {string} optionName - Name of the option (e.g., "Color", "Size")
   */
  renderDropdown(
    handle,
    index,
    optionValues,
    selectedValue,
    hasSwatch,
    optionName = "",
  ) {
    const popoverId = `BundleDropdown-${handle}-${index}-${this.sectionId}`;
    const triggerId = `BundleDropdownTrigger-${handle}-${index}-${this.sectionId}`;

    // Find selected option data
    const selectedEntry =
      optionValues.find(([value]) => value === selectedValue) ||
      optionValues[0];
    const [, selectedData] = selectedEntry;
    const selectedHex = selectedData?.hexColor;
    const selectedDisplayName =
      selectedData?.value || selectedValue || "Select";

    // Build options HTML
    let optionsHtml = "";
    optionValues.forEach(([value, optionData], optionIndex) => {
      const isSelected = value === selectedValue;
      const hexColor = optionData?.hexColor;
      const displayName = optionData?.value || value;
      const description = optionData?.shadeDescription || "";
      const available = optionData?.available !== false;

      optionsHtml += `
        <div
          class="bundle-dropdown__option"
          role="option"
          aria-selected="${isSelected}"
          aria-disabled="${!available}"
          data-option-value="${this.escapeHtml(value)}"
          data-variant-id="${optionData?.variantId || ""}"
          data-option-index="${optionIndex}"
          tabindex="${isSelected ? "0" : "-1"}"
        >
          ${hasSwatch && hexColor ? `<span class="bundle-dropdown__swatch" style="--swatch-color: ${this.normalizeHex(hexColor)}"></span>` : ""}
          <span class="bundle-dropdown__option-content">
            <span class="bundle-dropdown__option-name">${this.escapeHtml(displayName)}</span>
            ${description ? `<span class="bundle-dropdown__option-description">${this.escapeHtml(description)}</span>` : ""}
          </span>
        </div>
      `;
    });

    // Generate unique anchor name for this dropdown
    const anchorName = `--dropdown-anchor-${handle}-${index}`.replace(
      /[^a-zA-Z0-9-]/g,
      "-",
    );

    // Get description for display below dropdown (per Figma design)
    const selectedDescription = selectedData?.shadeDescription || "";

    return `
      <div class="bundle-dropdown" data-dropdown-handle="${handle}" data-option-name="${this.escapeHtml(optionName)}">
        <button
          type="button"
          id="${triggerId}"
          class="bundle-dropdown__trigger"
          style="anchor-name: ${anchorName}"
          aria-haspopup="listbox"
          aria-expanded="false"
          aria-controls="${popoverId}"
          popovertarget="${popoverId}"
        >
          <span class="bundle-dropdown__selected">
            ${hasSwatch && selectedHex ? `<span class="bundle-dropdown__swatch" style="--swatch-color: ${this.normalizeHex(selectedHex)}"></span>` : ""}
            <span class="bundle-dropdown__selected-name">${this.escapeHtml(selectedDisplayName)}</span>
          </span>
          <svg class="bundle-dropdown__chevron" viewBox="3 3 18 18" fill="none" stroke="currentColor" stroke-width="1">
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </button>
        <div
          id="${popoverId}"
          popover
          role="listbox"
          aria-labelledby="${triggerId}"
          class="bundle-dropdown__panel"
          style="position-anchor: ${anchorName}"
          data-selected-value="${this.escapeHtml(selectedValue)}"
        >
          ${optionsHtml}
        </div>
        ${
          selectedDescription
            ? `
          <div class="bundle-dropdown__description" data-description-for="${popoverId}">
            <span class="bundle-dropdown__description-text">${this.escapeHtml(selectedDescription)}</span>
          </div>
        `
            : ""
        }
      </div>
    `;
  }

  /**
   * Renders info display for single-variant components (no dropdown)
   */
  renderSingleVariantInfo(title, description, quantity = 1) {
    const quantityHtml = this.renderComponentQuantity(quantity);

    return `
      <div class="bundle-component__info">
        ${title ? `<span>${this.escapeHtml(title)}${quantityHtml ? `&nbsp;${quantityHtml}` : ""}</span>` : quantityHtml}
        ${description ? `<br><span>${this.escapeHtml(description)}</span>` : ""}
      </div>
    `;
  }

  /**
   * Renders a component quantity multiplier when more than one is included.
   */
  renderComponentQuantity(quantity) {
    const componentQuantity = Number(quantity);

    if (!Number.isFinite(componentQuantity) || componentQuantity <= 1) {
      return "";
    }

    return `×&nbsp;${this.escapeHtml(String(componentQuantity))}`;
  }

  /**
   * Renders quantity below an interactive component option picker.
   */
  renderComponentQuantityInfo(quantity) {
    const quantityHtml = this.renderComponentQuantity(quantity);

    return quantityHtml
      ? `<div class="bundle-component__info">${quantityHtml}</div>`
      : "";
  }

  /**
   * Initializes dropdown event handlers
   */
  initializeDropdowns() {
    const dropdowns = this.container.querySelectorAll(".bundle-dropdown");

    dropdowns.forEach((dropdown) => {
      const trigger = dropdown.querySelector(".bundle-dropdown__trigger");
      const panel = dropdown.querySelector(".bundle-dropdown__panel");
      const handle = dropdown.dataset.dropdownHandle;

      if (!trigger || !panel) return;

      // Track popover state via toggle event
      panel.addEventListener("toggle", (e) => {
        const isOpen = panel.matches(":popover-open");
        trigger.setAttribute("aria-expanded", isOpen);

        if (isOpen) {
          // Focus the selected option when opened
          const selectedOption = panel.querySelector('[aria-selected="true"]');
          if (selectedOption) {
            selectedOption.focus();
          }
        }
      });

      // Keyboard navigation for trigger
      trigger.addEventListener("keydown", (e) => {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          e.preventDefault();
          panel.showPopover();
        }
      });

      // Keyboard navigation for options
      panel.addEventListener("keydown", (e) => {
        this.handleDropdownKeydown(e, panel, trigger);
      });

      // Option click/selection
      panel.addEventListener("click", (e) => {
        const option = e.target.closest(".bundle-dropdown__option");
        if (option && option.getAttribute("aria-disabled") !== "true") {
          this.selectOption(dropdown, option, panel, trigger);
        }
      });

      this.dropdowns.set(handle, { trigger, panel, dropdown });
    });
  }

  /**
   * Handles keyboard navigation within dropdown
   */
  handleDropdownKeydown(e, panel, trigger) {
    const options = [
      ...panel.querySelectorAll(
        ".bundle-dropdown__option:not([aria-disabled='true'])",
      ),
    ];
    const currentIndex = options.findIndex(
      (opt) => opt === document.activeElement,
    );

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (currentIndex < options.length - 1) {
          options[currentIndex + 1].focus();
        }
        break;

      case "ArrowUp":
        e.preventDefault();
        if (currentIndex > 0) {
          options[currentIndex - 1].focus();
        }
        break;

      case "Home":
        e.preventDefault();
        options[0]?.focus();
        break;

      case "End":
        e.preventDefault();
        options[options.length - 1]?.focus();
        break;

      case "Enter":
      case " ":
        e.preventDefault();
        const focusedOption = document.activeElement;
        if (focusedOption?.classList.contains("bundle-dropdown__option")) {
          const dropdown = panel.closest(".bundle-dropdown");
          this.selectOption(dropdown, focusedOption, panel, trigger);
        }
        break;

      case "Escape":
        panel.hidePopover();
        trigger.focus();
        break;

      case "Tab":
        panel.hidePopover();
        break;
    }
  }

  /**
   * Handles option selection
   */
  selectOption(dropdown, option, panel, trigger) {
    const handle = dropdown.dataset.dropdownHandle;
    const optionName = dropdown.dataset.optionName;
    const optionValue = option.dataset.optionValue;

    // Update aria-selected states
    panel.querySelectorAll(".bundle-dropdown__option").forEach((opt) => {
      opt.setAttribute("aria-selected", "false");
      opt.setAttribute("tabindex", "-1");
    });
    option.setAttribute("aria-selected", "true");
    option.setAttribute("tabindex", "0");

    // Update trigger display
    const swatchSpan = option.querySelector(".bundle-dropdown__swatch");
    const nameSpan = option.querySelector(".bundle-dropdown__option-name");
    const selectedContainer = trigger.querySelector(
      ".bundle-dropdown__selected",
    );

    if (selectedContainer) {
      const triggerSwatch = selectedContainer.querySelector(
        ".bundle-dropdown__swatch",
      );
      const triggerName = selectedContainer.querySelector(
        ".bundle-dropdown__selected-name",
      );

      if (triggerSwatch && swatchSpan) {
        triggerSwatch.style.setProperty(
          "--swatch-color",
          swatchSpan.style.getPropertyValue("--swatch-color"),
        );
      }
      if (triggerName && nameSpan) {
        triggerName.textContent = nameSpan.textContent;
      }
    }

    // Update selected option value
    panel.dataset.selectedValue = optionValue;

    // Update shade description below dropdown if present
    const descriptionEl = dropdown.querySelector(
      ".bundle-dropdown__description-text",
    );
    const optionDescription = option.querySelector(
      ".bundle-dropdown__option-description",
    );
    if (descriptionEl && optionDescription) {
      descriptionEl.textContent = optionDescription.textContent;
    }

    // Close popover
    panel.hidePopover();
    trigger.focus();

    // Find the matching variant based on ALL selected options for this component
    const matchedVariantId = this.findMatchingVariant(handle);

    if (matchedVariantId) {
      // Update component image based on selected variant
      this.updateComponentImageForVariant(handle, matchedVariantId);

      // Dispatch custom event for form sync
      this.dispatchVariantChange(handle, matchedVariantId);
      console.log(
        `[BundleDropdown] Selected variant ${matchedVariantId} for ${handle} (${optionName}: ${optionValue})`,
      );
    } else {
      console.warn(`[BundleDropdown] No matching variant found for ${handle}`);
    }
  }

  /**
   * Updates component image when variant changes using swatch data
   * Falls back to product featured image if variant has no image
   * @param {string} handle - Product handle
   * @param {string} variantId - Selected variant ID
   */
  updateComponentImageForVariant(handle, variantId) {
    const swatchData = this.swatchDataMap?.get(handle);
    if (!swatchData) {
      console.warn(
        `[BundleDropdown] No swatch data for ${handle}, cannot update image`,
      );
      return;
    }

    const variantData = swatchData.variants?.[variantId];

    // Get variant image, fallback to product featured image
    const imageUrl = variantData?.imageUrl || swatchData.featuredImageUrl;
    const imageAlt =
      variantData?.imageAlt ||
      swatchData.featuredImageAlt ||
      swatchData.productTitle;

    if (imageUrl) {
      this.updateComponentImage(handle, imageUrl, imageAlt);
      console.log(`[BundleDropdown] Updated image for ${handle}: ${imageUrl}`);
    } else {
      console.warn(
        `[BundleDropdown] No image available for ${handle} variant ${variantId}`,
      );
    }
  }

  /**
   * Finds the variant ID that matches all selected options for a component
   * @param {string} handle - Product handle
   * @returns {string|null} - Matching variant ID or null
   */
  findMatchingVariant(handle) {
    const variants = this.componentVariants.get(handle);
    if (!variants || variants.length === 0) {
      console.warn(`[BundleDropdown] No variants stored for ${handle}`);
      return null;
    }

    // Get all dropdowns for this component
    const component = this.container.querySelector(
      `[data-component-handle="${handle}"]`,
    );
    if (!component) {
      console.warn(`[BundleDropdown] Component not found for ${handle}`);
      return null;
    }

    const dropdowns = component.querySelectorAll(".bundle-dropdown");
    if (dropdowns.length === 0) {
      console.warn(`[BundleDropdown] No dropdowns found for ${handle}`);
      return null;
    }

    // Gather selected option values from all dropdowns
    const selectedOptions = {};
    dropdowns.forEach((dropdown) => {
      const optionName = dropdown.dataset.optionName;
      const panel = dropdown.querySelector(".bundle-dropdown__panel");
      const selectedValue = panel?.dataset.selectedValue;

      if (optionName && selectedValue) {
        selectedOptions[optionName] = selectedValue;
      }
    });

    console.log(
      `[BundleDropdown] Finding variant for ${handle} with options:`,
      selectedOptions,
    );

    // Find variant matching all selected options
    for (const [variantId, variantData] of variants) {
      let matches = true;

      for (const [optionName, optionValue] of Object.entries(selectedOptions)) {
        // Check option1, option2, option3 against the option name
        const matchesOption1 = variantData.option1 === optionValue;
        const matchesOption2 = variantData.option2 === optionValue;
        const matchesOption3 = variantData.option3 === optionValue;

        if (!matchesOption1 && !matchesOption2 && !matchesOption3) {
          matches = false;
          break;
        }
      }

      if (matches) {
        console.log(`[BundleDropdown] Found matching variant: ${variantId}`);
        return variantId;
      }
    }

    return null;
  }

  /**
   * Dispatches variant change event for form sync
   */
  dispatchVariantChange(handle, variantId) {
    // Store the selection on the component element
    const component = this.container.querySelector(
      `[data-component-handle="${handle}"]`,
    );
    if (component) {
      component.dataset.selectedVariantId = variantId;
    }

    // Find the matching BUNDLE variant based on all current component selections
    const matchingBundleVariantId = this.findMatchingBundleVariant();

    if (matchingBundleVariantId) {
      const previousBundleVariantId = this.container.dataset.variantId;

      // Update the form's main variant ID input
      const productForm = document.querySelector(
        `product-form-component[data-product-id="${this.productId}"] form`,
      );
      if (productForm) {
        const variantInput = productForm.querySelector('input[name="id"]');
        if (variantInput) {
          const previousValue = variantInput.value;
          variantInput.value = matchingBundleVariantId;
          console.log(
            `[BundleDropdown] Updated form variant ID: ${previousValue} → ${matchingBundleVariantId}`,
          );

          // Also update the URL
          const url = new URL(window.location.href);
          url.searchParams.set("variant", matchingBundleVariantId);
          history.replaceState({}, "", url.toString());

          // Update the data-variant-id on the picker container
          this.container.dataset.variantId = matchingBundleVariantId;
        }
      }

      if (matchingBundleVariantId !== previousBundleVariantId) {
        this.fetchUpdatedBundleVariant(matchingBundleVariantId);
      }
    } else {
      console.warn(
        `[BundleDropdown] No matching bundle variant found for component selections`,
      );
    }

    // Dispatch a custom event for any listeners
    const event = new CustomEvent("bundle:component:change", {
      bubbles: true,
      detail: {
        handle,
        componentVariantId: variantId,
        bundleVariantId: matchingBundleVariantId,
        sectionId: this.sectionId,
        allSelections: this.getAllComponentSelections(),
      },
    });

    this.container.dispatchEvent(event);

    console.log(
      `[BundleDropdown] Component ${handle} changed to variant ${variantId}, bundle variant: ${matchingBundleVariantId}`,
    );
  }

  /**
   * Fetches the selected bundle variant and dispatches the theme's standard
   * variant update event so price and product-form components stay in sync.
   * @param {string} bundleVariantId - Selected bundle variant ID
   */
  async fetchUpdatedBundleVariant(bundleVariantId) {
    if (!this.productUrl || !this.sectionId) {
      console.warn(
        "[BundleDropdown] Missing product URL or section ID; cannot update variant content",
      );
      return;
    }

    this.variantUpdateAbortController?.abort();
    this.variantUpdateAbortController = new AbortController();
    const { signal } = this.variantUpdateAbortController;

    try {
      const url = new URL(this.productUrl, window.location.origin);
      url.searchParams.set("variant", bundleVariantId);
      url.searchParams.set("section_id", this.sectionId);

      const response = await fetch(url.toString(), { signal });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const responseText = await response.text();
      if (signal.aborted) return;

      const html = new DOMParser().parseFromString(responseText, "text/html");
      const bundleVariant = this.allBundleVariants.find(
        (variant) => variant.id === bundleVariantId,
      );

      this.container.dispatchEvent(
        new VariantUpdateEvent(
          bundleVariant || { id: bundleVariantId, available: true },
          bundleVariantId,
          {
            html,
            productId: this.productId,
          },
        ),
      );
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error(
          `[BundleDropdown] Failed to update bundle variant ${bundleVariantId}:`,
          error,
        );
      }
    }
  }

  /**
   * Gets all current component variant selections
   * @returns {Object} Map of handle -> variantId
   */
  getAllComponentSelections() {
    const selections = {};
    const components = this.container.querySelectorAll(
      "[data-component-handle]",
    );

    components.forEach((component) => {
      const handle = component.dataset.componentHandle;
      const variantId = component.dataset.selectedVariantId;
      if (handle && variantId) {
        selections[handle] = variantId;
      }
    });

    return selections;
  }

  /**
   * Updates component image when variant changes
   */
  updateComponentImage(handle, imageUrl, imageAlt) {
    const component = this.container.querySelector(
      `[data-component-handle="${handle}"]`,
    );
    if (!component) return;

    const img = component.querySelector(".bundle-component__image img");
    if (img && imageUrl) {
      img.src = imageUrl;
      img.alt = imageAlt || "";
    }
  }

  /**
   * Normalizes hex color value
   */
  normalizeHex(hex) {
    if (!hex) return "#ccc";
    const trimmed = hex.trim();
    return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  }

  /**
   * Escapes HTML special characters
   */
  escapeHtml(str) {
    if (!str) return "";
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }
}

/**
 * Main Bundle PDP Controller class (legacy swatch injection)
 */
class BundlePDPController {
  /**
   * @param {HTMLElement} variantPicker - The variant-picker element
   */
  constructor(variantPicker) {
    this.variantPicker = variantPicker;
    this.productId = variantPicker.dataset.productId;
    this.initialized = false;
    this.componentData = null;
    this.swatchDataCache = new Map();
    this.swatchDataMap = null; // Store for re-injection
    this.dataInjected = false;
    this.observer = null;

    // Set up MutationObserver to detect DOM re-renders from Section Rendering API
    this.setupMutationObserver();
  }

  /**
   * Sets up a MutationObserver to detect when variant picker HTML is re-rendered
   * and re-inject cached data
   */
  setupMutationObserver() {
    this.observer = new MutationObserver((mutations) => {
      // Check if we have cached data to re-inject
      if (!this.componentData || !this.swatchDataMap) return;

      // Look for skeleton elements that indicate a re-render
      const hasSkeletons = this.variantPicker.querySelector(
        "[data-bundle-component-name].skeleton",
      );

      if (hasSkeletons) {
        console.log("[BundlePDP] DOM re-rendered, re-injecting cached data");
        this.injectComponentData(this.componentData, this.swatchDataMap);
        this.deactivateSkeletonState();
      }
    });

    this.observer.observe(this.variantPicker, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Initializes the controller for the current variant
   * @param {string|number} variantId
   */
  async init(variantId) {
    if (!variantId) {
      console.warn("[BundlePDP] No variant ID provided");
      return;
    }

    // If we already have data injected, don't re-fetch
    // Bundle structure doesn't change between variant selections
    if (this.dataInjected) {
      console.info("[BundlePDP] Using cached data, skipping re-fetch");
      return;
    }

    this.activateSkeletonState();

    // Check sessionStorage cache first for bundle components
    const cacheKey = `product_${this.productId}`;
    let bundleData = BundleCache.get(cacheKey);

    // Validate cached data has actual components
    if (
      bundleData &&
      (!bundleData.components || bundleData.components.length === 0)
    ) {
      console.warn(
        "[BundlePDP] Cached data has no components, invalidating cache",
      );
      bundleData = null;
    }

    if (!bundleData) {
      // Phase 1: Fetch bundle component structure
      bundleData = await fetchBundleComponents(variantId);

      // Only cache if we have valid component data
      if (bundleData?.requiresComponents && bundleData.components?.length > 0) {
        BundleCache.set(cacheKey, bundleData);
      }
    } else {
      console.info("[BundlePDP] Using sessionStorage cached bundle data");
    }

    if (
      !bundleData ||
      !bundleData.requiresComponents ||
      !bundleData.components?.length
    ) {
      this.deactivateSkeletonState();
      this.hideComponentNames();
      console.warn("[BundlePDP] No valid bundle components found");
      return;
    }

    this.componentData = bundleData.components;

    // Extract unique product handles
    const productHandles = [
      ...new Set(
        bundleData.components.map((c) => c.productVariant.product.handle),
      ),
    ];

    // Phase 2: Fetch swatch data for each component product
    const swatchResults = await Promise.all(
      productHandles.map(async (handle) => {
        // Check memory cache
        if (this.swatchDataCache.has(handle)) {
          return { handle, data: this.swatchDataCache.get(handle) };
        }

        // Check sessionStorage cache
        const cachedSwatch = BundleCache.get(`swatch_${handle}`);
        if (cachedSwatch) {
          this.swatchDataCache.set(handle, cachedSwatch);
          return { handle, data: cachedSwatch };
        }

        // Fetch from API
        const data = await fetchSwatchData(handle);
        if (data) {
          this.swatchDataCache.set(handle, data);
          BundleCache.set(`swatch_${handle}`, data);
        }
        return { handle, data };
      }),
    );

    // Check for failures
    const failures = swatchResults.filter((r) => r.data === null);
    if (failures.length > 0) {
      showErrorToast(
        "Unable to load some product information. Please refresh the page.",
      );
    }

    // Build handle -> swatch data map
    const swatchDataMap = new Map(
      swatchResults
        .filter((r) => r.data !== null)
        .map((r) => [r.handle, r.data]),
    );

    // Store for re-injection after DOM re-renders
    this.swatchDataMap = swatchDataMap;

    // Inject data into DOM
    this.injectComponentData(bundleData.components, swatchDataMap);
    this.deactivateSkeletonState();
    this.initialized = true;
    this.dataInjected = true;
  }

  /**
   * Activates skeleton loading state on component name elements, images, and swatches
   */
  activateSkeletonState() {
    // Component name elements
    const nameEls = this.variantPicker.querySelectorAll(
      "[data-bundle-component-name]",
    );
    nameEls.forEach((nameEl) => {
      nameEl.classList.add("skeleton");
      nameEl.textContent = "\u00A0"; // Non-breaking space to maintain height
    });

    // Component image elements
    const imageEls = this.variantPicker.querySelectorAll(
      "[data-bundle-component-image]",
    );
    imageEls.forEach((imageEl) => imageEl.classList.add("skeleton"));

    // Swatch elements
    const fieldsets = this.variantPicker.querySelectorAll(
      "fieldset.variant-option--swatches",
    );
    fieldsets.forEach((fieldset) => {
      const swatches = fieldset.querySelectorAll(".swatch");
      swatches.forEach((swatch) => swatch.classList.add("skeleton"));
    });
  }

  /**
   * Deactivates skeleton loading state
   */
  deactivateSkeletonState() {
    const nameEls = this.variantPicker.querySelectorAll(
      "[data-bundle-component-name]",
    );
    nameEls.forEach((nameEl) => nameEl.classList.remove("skeleton"));

    const imageEls = this.variantPicker.querySelectorAll(
      "[data-bundle-component-image]",
    );
    imageEls.forEach((imageEl) => imageEl.classList.remove("skeleton"));

    const fieldsets = this.variantPicker.querySelectorAll(
      "fieldset.variant-option--swatches",
    );
    fieldsets.forEach((fieldset) => {
      const swatches = fieldset.querySelectorAll(".swatch");
      swatches.forEach((swatch) => swatch.classList.remove("skeleton"));
    });
  }

  /**
   * Hides component name elements (for non-bundle products)
   */
  hideComponentNames() {
    const nameEls = this.variantPicker.querySelectorAll(
      "[data-bundle-component-name]",
    );
    nameEls.forEach((el) => {
      el.textContent = "";
      el.classList.remove("skeleton");
    });
  }

  /**
   * Injects component names and swatch colors into the DOM
   * @param {Array} components - Bundle component data from Storefront API
   * @param {Map} swatchDataMap - Map of product handle to swatch data
   */
  injectComponentData(components, swatchDataMap) {
    console.log("[BundlePDP] Injecting component data:", {
      componentsCount: components.length,
      swatchDataHandles: [...swatchDataMap.keys()],
    });

    // Get all fieldsets with component indices
    const allFieldsets = this.variantPicker.querySelectorAll(
      "fieldset[data-component-index]",
    );

    // Build a map of fieldset -> legend text (option name) for matching
    // Shopify bundle options are named after component products
    const fieldsetLegendMap = new Map();
    allFieldsets.forEach((fieldset) => {
      const index = fieldset.dataset.componentIndex;
      const legend = fieldset.querySelector("legend");
      // Get the raw text, excluding the bundle-component-name span we inject
      let legendText = "";
      if (legend) {
        // Clone to avoid modifying the DOM
        const legendClone = legend.cloneNode(true);
        const componentNameSpan = legendClone.querySelector(
          "[data-bundle-component-name]",
        );
        if (componentNameSpan) componentNameSpan.remove();
        legendText = legendClone.textContent?.trim() || "";
      }
      fieldsetLegendMap.set(fieldset, { index, legendText });
    });

    console.log(
      "[BundlePDP] Fieldset legends:",
      [...fieldsetLegendMap.entries()].map(([f, data]) => ({
        index: data.index,
        legend: data.legendText,
      })),
    );

    // Track unmatched components (single-variant) for later injection
    const unmatchedComponents = [];

    // For each component, find the matching fieldset by comparing product title to legend text
    components.forEach((component) => {
      const productTitle = component.productVariant.product.title;
      const productHandle = component.productVariant.product.handle;
      const swatchData = swatchDataMap.get(productHandle);

      if (!swatchData) {
        console.warn(`[BundlePDP] No swatch data for ${productHandle}`);
        return;
      }

      console.log(
        `[BundlePDP] Looking for fieldset matching "${productTitle}" (${productHandle})`,
      );

      // Find the fieldset whose legend contains the product title
      let matchedFieldset = null;
      let matchedIndex = null;
      let bestMatchScore = 0;

      for (const [fieldset, data] of fieldsetLegendMap.entries()) {
        const legendLower = data.legendText.toLowerCase();
        const titleLower = productTitle.toLowerCase();

        // Check if legend contains product title or vice versa
        let matchScore = 0;
        if (legendLower.includes(titleLower)) {
          matchScore = titleLower.length; // Longer match = better
        } else if (titleLower.includes(legendLower) && legendLower.length > 0) {
          matchScore = legendLower.length;
        }

        if (matchScore > bestMatchScore) {
          bestMatchScore = matchScore;
          matchedFieldset = fieldset;
          matchedIndex = data.index;
        }
      }

      if (matchedFieldset && bestMatchScore > 0) {
        console.log(
          `[BundlePDP] Matched "${productTitle}" to fieldset index ${matchedIndex} (score: ${bestMatchScore})`,
        );
        // Remove from pool so it can't be matched again
        fieldsetLegendMap.delete(matchedFieldset);
      } else {
        matchedFieldset = null;
      }

      if (!matchedFieldset) {
        // This is a single-variant component (no fieldset rendered by Shopify)
        console.log(
          `[BundlePDP] No fieldset for "${productTitle}" - will create single-variant display`,
        );
        unmatchedComponents.push({ component, swatchData });
        return;
      }

      // Inject component name into the matched fieldset's legend
      const nameEl = matchedFieldset.querySelector(
        "[data-bundle-component-name]",
      );
      if (nameEl) {
        nameEl.textContent = productTitle;
        nameEl.classList.remove("skeleton");
        console.log(
          `[BundlePDP] Injected name "${productTitle}" for ${productHandle}`,
        );
      }

      // Inject component image
      const imageEl = matchedFieldset.querySelector(
        "[data-bundle-component-image]",
      );
      if (imageEl) {
        // Get image URL: prefer variant image, fallback to product featured image
        const variantImage = component.productVariant.image;
        const productImage = component.productVariant.product.featuredImage;
        const imageData = variantImage || productImage;

        if (imageData?.url) {
          const img = imageEl.querySelector("img");
          if (img) {
            img.src = imageData.url;
            img.alt = imageData.altText || productTitle;
          }
          // Set link to component product page
          imageEl.href = `/products/${productHandle}`;
          imageEl.classList.remove("skeleton");
          console.log(`[BundlePDP] Injected image for ${productHandle}`);
        } else {
          // No image available, hide the element
          imageEl.style.display = "none";
          console.log(`[BundlePDP] No image available for ${productHandle}`);
        }
      }

      // Inject swatch colors if product has swatch data
      if (swatchData.hasSwatches && swatchData.variants) {
        const swatchLabels = matchedFieldset.querySelectorAll(
          "[data-bundle-swatch-variant-id]",
        );

        // Convert variants object to array for index-based matching within this fieldset
        const variantDataArray = Object.values(swatchData.variants);

        swatchLabels.forEach((label, swatchIndex) => {
          const variantData = variantDataArray[swatchIndex];

          if (variantData?.hexColor) {
            const swatch = label.querySelector(".swatch");
            if (swatch) {
              // Handle hex values with or without # prefix
              const hexValue = variantData.hexColor.startsWith("#")
                ? variantData.hexColor.trim()
                : `#${variantData.hexColor.trim()}`;
              swatch.style.setProperty("--swatch-background", hexValue);
              swatch.classList.remove("skeleton");
              console.log(
                `[BundlePDP] Applied color ${hexValue} to ${productHandle} swatch ${swatchIndex}`,
              );
            }
          }
        });
      } else {
        // Product doesn't have swatch data (e.g., parfum with only sizes)
        // Just remove skeleton state from swatches
        const swatches = matchedFieldset.querySelectorAll(".swatch");
        swatches.forEach((swatch) => swatch.classList.remove("skeleton"));
        console.log(
          `[BundlePDP] ${productHandle} has no swatches, removed skeleton state`,
        );
      }
    });

    // Inject single-variant component displays at the end
    if (unmatchedComponents.length > 0) {
      this.injectSingleVariantComponents(unmatchedComponents);
    }
  }

  /**
   * Creates and injects static displays for single-variant components
   * These components only have one variant, so Shopify doesn't render a fieldset for them
   * @param {Array} unmatchedComponents - Array of { component, swatchData } objects
   */
  injectSingleVariantComponents(unmatchedComponents) {
    console.log(
      `[BundlePDP] Injecting ${unmatchedComponents.length} single-variant component(s)`,
    );

    // Remove any existing single-variant displays (for re-injection after DOM re-render)
    const existingDisplays = this.variantPicker.querySelectorAll(
      ".bundle-single-variant",
    );
    existingDisplays.forEach((el) => el.remove());

    // Find the form or last fieldset to append after
    const form = this.variantPicker.querySelector("form");
    const lastFieldset = this.variantPicker.querySelector(
      "fieldset:last-of-type",
    );
    const insertTarget = lastFieldset || form;

    if (!insertTarget) {
      console.warn(
        "[BundlePDP] No insertion target found for single-variant components",
      );
      return;
    }

    unmatchedComponents.forEach(({ component, swatchData }) => {
      const productTitle = component.productVariant.product.title;
      const productHandle = component.productVariant.product.handle;
      const variantTitle = component.productVariant.title;
      const selectedOptions = component.productVariant.selectedOptions;

      // Get image: prefer variant image, fallback to product featured image
      const variantImage = component.productVariant.image;
      const productImage = component.productVariant.product.featuredImage;
      const imageData = variantImage || productImage;

      // Create the single-variant display element
      const displayEl = document.createElement("div");
      displayEl.className = "bundle-single-variant variant-option";
      displayEl.setAttribute("data-bundle-single-variant", productHandle);

      // Build the HTML structure to match fieldset styling
      let html = `
        <div class="bundle-single-variant__content">
          <span class="bundle-component-name">${this.escapeHtml(productTitle)}</span>
      `;

      // Add image if available
      if (imageData?.url) {
        html += `
          <a class="bundle-component-image" href="/products/${productHandle}">
            <img src="${imageData.url}" alt="${this.escapeHtml(imageData.altText || productTitle)}" loading="lazy" width="150" height="150" />
          </a>
        `;
      }

      // Add variant option display (informational only)
      // Future: To show option values like "3 Casablanca / 3.0", uncomment below:
      // if (selectedOptions && selectedOptions.length > 0) {
      //   const optionValues = selectedOptions.map(opt => opt.value).join(' / ');
      //   html += `<span class="bundle-single-variant__option">${this.escapeHtml(optionValues)}</span>`;
      // }

      html += `</div>`;
      displayEl.innerHTML = html;

      // Insert after the last fieldset (or into the form)
      if (lastFieldset) {
        lastFieldset.insertAdjacentElement("afterend", displayEl);
      } else if (form) {
        form.appendChild(displayEl);
      }

      console.log(
        `[BundlePDP] Injected single-variant display for "${productTitle}"`,
      );
    });
  }

  /**
   * Disconnects the MutationObserver and releases cached references.
   * Call when the host element is removed from the DOM.
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.componentData = null;
    this.swatchDataMap = null;
    this.swatchDataCache.clear();
  }

  /**
   * Escapes HTML special characters to prevent XSS
   * @param {string} str
   * @returns {string}
   */
  escapeHtml(str) {
    if (!str) return "";
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }
}

/**
 * Initializes Bundle PDP functionality
 * Detects which picker type is present and uses the appropriate controller
 */
function initBundlePDP() {
  // First, check for new dropdown-based bundle variant pickers
  const dropdownPickers = document.querySelectorAll(
    "[data-bundle-variant-picker]",
  );

  dropdownPickers.forEach((picker) => {
    // Skip if already initialized
    if (picker.bundleDropdownController) return;

    // Get variant ID directly from data attribute (set by Liquid)
    const initialVariantId = picker.dataset.variantId;

    // Create dropdown controller and initialize
    const controller = new BundleDropdownController(picker);
    picker.bundleDropdownController = controller;

    console.log("[BundleDropdown] Initialized dropdown controller", {
      productId: picker.dataset.productId,
      sectionId: picker.dataset.sectionId,
      initialVariantId,
    });

    if (initialVariantId) {
      controller.init(initialVariantId);
    } else {
      console.warn(
        "[BundleDropdown] No initial variant ID found - cannot initialize",
      );
    }
  });

  // Also check for legacy swatch-based bundle variant pickers
  const legacyPickers = document.querySelectorAll(
    'variant-picker[data-is-bundle="true"]',
  );

  legacyPickers.forEach((picker) => {
    // Skip if already initialized or if using new dropdown picker
    if (picker.bundlePDPController) return;
    if (picker.querySelector("[data-bundle-variant-picker]")) return;

    // Get initial variant ID from the JSON script in the form
    const variantScript = picker.querySelector(
      'form script[type="application/json"]',
    );
    let initialVariantId = null;

    if (variantScript) {
      try {
        const variantData = JSON.parse(variantScript.textContent);
        initialVariantId = variantData?.id;
      } catch (e) {
        console.warn("[BundlePDP] Could not parse initial variant data");
      }
    }

    // Create controller and initialize
    const controller = new BundlePDPController(picker);
    picker.bundlePDPController = controller;

    if (initialVariantId) {
      controller.init(initialVariantId);
    }

    // Listen for variant changes - but data is cached, so this will be a no-op
    // after initial load. Kept for potential future use cases.
    picker.addEventListener("variant:change", (event) => {
      const newVariantId = event.detail?.variant?.id;
      if (newVariantId) {
        controller.init(newVariantId);
      }
    });
  });
}

// Initialize on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initBundlePDP);
} else {
  initBundlePDP();
}

// Re-initialize on Shopify section load (for theme editor)
document.addEventListener("shopify:section:load", initBundlePDP);

export { BundleDropdownController, BundlePDPController, initBundlePDP };
